/**
 * Panel Configuration Store
 *
 * Zustand store for managing panel configurations with persistence.
 * Syncs with backend API when available, falls back to localStorage in demo mode.
 *
 * @version 1.0.0
 * @date January 12, 2026
 * @task T-S23-005
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist, createJSONStorage } from 'zustand/middleware';
import panelConfigApi, {
  PanelConfiguration,
  PanelType,
  UpsertPanelConfigRequest,
  PanelLayoutTemplate,
} from '../api/panelConfig';

// ============================================================================
// TYPES
// ============================================================================

export interface PanelState {
  panelId: string;
  panelType: PanelType;
  position: { x: number; y: number };
  size: { width: number; height: number };
  isVisible: boolean;
  isMinimized: boolean;
  isMaximized: boolean;
  zIndex: number;
  config: Record<string, unknown>;
}

interface PanelConfigState {
  // Panel configurations by view context
  configsByView: Record<string, Record<string, PanelState>>;
  
  // Templates
  templates: PanelLayoutTemplate[];
  
  // Loading states
  isLoading: boolean;
  isSaving: boolean;
  lastSyncedAt: string | null;
  error: string | null;
  
  // Current view context
  currentView: string;
  
  // API availability
  isApiAvailable: boolean;
}

interface PanelConfigActions {
  // Panel CRUD
  setPanel: (viewContext: string, panel: PanelState) => void;
  updatePanelPosition: (viewContext: string, panelId: string, x: number, y: number) => void;
  updatePanelSize: (viewContext: string, panelId: string, width: number, height: number) => void;
  updatePanelVisibility: (viewContext: string, panelId: string, isVisible: boolean) => void;
  updatePanelZIndex: (viewContext: string, panelId: string, zIndex: number) => void;
  updatePanelConfig: (viewContext: string, panelId: string, config: Record<string, unknown>) => void;
  togglePanelMinimized: (viewContext: string, panelId: string) => void;
  togglePanelMaximized: (viewContext: string, panelId: string) => void;
  removePanel: (viewContext: string, panelId: string) => void;
  
  // Bulk operations
  bringToFront: (viewContext: string, panelId: string) => void;
  resetPanelsForView: (viewContext: string) => void;
  
  // Sync with backend
  loadFromApi: (viewContext?: string) => Promise<void>;
  saveToApi: (viewContext: string) => Promise<void>;
  applyTemplate: (templateId: string) => Promise<void>;
  loadTemplates: (viewContext?: string) => Promise<void>;
  
  // View context
  setCurrentView: (viewContext: string) => void;
  
  // Getters
  getPanelsForView: (viewContext: string) => PanelState[];
  getPanel: (viewContext: string, panelId: string) => PanelState | undefined;
  
  // API availability
  setApiAvailable: (available: boolean) => void;
}

type PanelConfigStore = PanelConfigState & PanelConfigActions;

// ============================================================================
// DEFAULT PANEL LAYOUTS
// ============================================================================

const DEFAULT_NETWORK_PANELS: Record<string, PanelState> = {
  simulation_control: {
    panelId: 'simulation_control',
    panelType: 'simulation_control',
    position: { x: 50, y: 50 },
    size: { width: 300, height: 280 },
    isVisible: false, // Hidden until simulation is started
    isMinimized: false,
    isMaximized: false,
    zIndex: 100,
    config: { playbackSpeed: 1 },
  },
  attack_event_log: {
    panelId: 'attack_event_log',
    panelType: 'attack_event_log',
    position: { x: 50, y: 350 },
    size: { width: 400, height: 300 },
    isVisible: false,
    isMinimized: false,
    isMaximized: false,
    zIndex: 101,
    config: { autoScroll: true },
  },
  network_legend: {
    panelId: 'network_legend',
    panelType: 'network_legend',
    position: { x: 1120, y: 50 },
    size: { width: 200, height: 250 },
    isVisible: true,
    isMinimized: false,
    isMaximized: false,
    zIndex: 102,
    config: {},
  },
};

const DEFAULT_DASHBOARD_PANELS: Record<string, PanelState> = {
  system_activity: {
    panelId: 'system_activity',
    panelType: 'system_logs',
    position: { x: 800, y: 50 },
    size: { width: 450, height: 400 },
    isVisible: false,
    isMinimized: false,
    isMaximized: false,
    zIndex: 100,
    config: { logLevel: 'all', autoRefresh: true },
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function apiConfigToState(config: PanelConfiguration): PanelState {
  return {
    panelId: config.panel_id,
    panelType: config.panel_type,
    position: { x: config.position_x, y: config.position_y },
    size: { width: config.width, height: config.height },
    isVisible: config.is_visible,
    isMinimized: config.is_minimized,
    isMaximized: config.is_maximized,
    zIndex: config.z_index,
    config: config.config as Record<string, unknown>,
  };
}

function stateToApiRequest(state: PanelState, viewContext: string): UpsertPanelConfigRequest {
  return {
    panel_id: state.panelId,
    panel_type: state.panelType,
    position_x: state.position.x,
    position_y: state.position.y,
    width: state.size.width,
    height: state.size.height,
    is_visible: state.isVisible,
    is_minimized: state.isMinimized,
    is_maximized: state.isMaximized,
    z_index: state.zIndex,
    config: state.config,
    view_context: viewContext,
  };
}

// ============================================================================
// STORE
// ============================================================================

// Debounce timer for auto-save
let saveDebounceTimer: ReturnType<typeof setTimeout> | null = null;

export const usePanelConfigStore = create<PanelConfigStore>()(
  persist(
    immer((set, get) => ({
      // Initial state
      configsByView: {
        network: DEFAULT_NETWORK_PANELS,
        dashboard: DEFAULT_DASHBOARD_PANELS,
      },
      templates: [],
      isLoading: false,
      isSaving: false,
      lastSyncedAt: null,
      error: null,
      currentView: 'network',
      isApiAvailable: false,

      // Actions
      setPanel: (viewContext, panel) => {
        set((state) => {
          if (!state.configsByView[viewContext]) {
            state.configsByView[viewContext] = {};
          }
          state.configsByView[viewContext][panel.panelId] = panel;
        });
        
        // Auto-save after debounce
        if (get().isApiAvailable) {
          if (saveDebounceTimer) clearTimeout(saveDebounceTimer);
          saveDebounceTimer = setTimeout(() => {
            get().saveToApi(viewContext);
          }, 1000);
        }
      },

      updatePanelPosition: (viewContext, panelId, x, y) => {
        set((state) => {
          const panel = state.configsByView[viewContext]?.[panelId];
          if (panel) {
            panel.position = { x, y };
          }
        });
        
        // Auto-save with debounce
        if (get().isApiAvailable) {
          if (saveDebounceTimer) clearTimeout(saveDebounceTimer);
          saveDebounceTimer = setTimeout(() => {
            get().saveToApi(viewContext);
          }, 1000);
        }
      },

      updatePanelSize: (viewContext, panelId, width, height) => {
        set((state) => {
          const panel = state.configsByView[viewContext]?.[panelId];
          if (panel) {
            panel.size = { 
              width: Math.max(100, Math.min(2000, width)), 
              height: Math.max(50, Math.min(1500, height)) 
            };
          }
        });
        
        if (get().isApiAvailable) {
          if (saveDebounceTimer) clearTimeout(saveDebounceTimer);
          saveDebounceTimer = setTimeout(() => {
            get().saveToApi(viewContext);
          }, 1000);
        }
      },

      updatePanelVisibility: (viewContext, panelId, isVisible) => {
        set((state) => {
          const panel = state.configsByView[viewContext]?.[panelId];
          if (panel) {
            panel.isVisible = isVisible;
          }
        });
        
        if (get().isApiAvailable) {
          if (saveDebounceTimer) clearTimeout(saveDebounceTimer);
          saveDebounceTimer = setTimeout(() => {
            get().saveToApi(viewContext);
          }, 500);
        }
      },

      updatePanelZIndex: (viewContext, panelId, zIndex) => {
        set((state) => {
          const panel = state.configsByView[viewContext]?.[panelId];
          if (panel) {
            panel.zIndex = zIndex;
          }
        });
      },

      updatePanelConfig: (viewContext, panelId, config) => {
        set((state) => {
          const panel = state.configsByView[viewContext]?.[panelId];
          if (panel) {
            panel.config = { ...panel.config, ...config };
          }
        });
        
        if (get().isApiAvailable) {
          if (saveDebounceTimer) clearTimeout(saveDebounceTimer);
          saveDebounceTimer = setTimeout(() => {
            get().saveToApi(viewContext);
          }, 1000);
        }
      },

      togglePanelMinimized: (viewContext, panelId) => {
        set((state) => {
          const panel = state.configsByView[viewContext]?.[panelId];
          if (panel) {
            panel.isMinimized = !panel.isMinimized;
            if (panel.isMinimized) {
              panel.isMaximized = false;
            }
          }
        });
      },

      togglePanelMaximized: (viewContext, panelId) => {
        set((state) => {
          const panel = state.configsByView[viewContext]?.[panelId];
          if (panel) {
            panel.isMaximized = !panel.isMaximized;
            if (panel.isMaximized) {
              panel.isMinimized = false;
            }
          }
        });
      },

      removePanel: (viewContext, panelId) => {
        set((state) => {
          if (state.configsByView[viewContext]) {
            delete state.configsByView[viewContext][panelId];
          }
        });
      },

      bringToFront: (viewContext, panelId) => {
        set((state) => {
          const viewPanels = state.configsByView[viewContext];
          if (!viewPanels) return;
          
          // Find max z-index
          const maxZ = Math.max(...Object.values(viewPanels).map((p) => p.zIndex));
          
          const panel = viewPanels[panelId];
          if (panel && panel.zIndex < maxZ) {
            panel.zIndex = maxZ + 1;
          }
        });
      },

      resetPanelsForView: (viewContext) => {
        set((state) => {
          if (viewContext === 'network') {
            state.configsByView[viewContext] = { ...DEFAULT_NETWORK_PANELS };
          } else if (viewContext === 'dashboard') {
            state.configsByView[viewContext] = { ...DEFAULT_DASHBOARD_PANELS };
          } else {
            state.configsByView[viewContext] = {};
          }
        });
      },

      loadFromApi: async (viewContext) => {
        set({ isLoading: true, error: null });
        
        try {
          const configs = await panelConfigApi.listPanelConfigs(viewContext);
          
          set((state) => {
            // Group by view context
            const byView: Record<string, Record<string, PanelState>> = {};
            
            for (const config of configs) {
              if (!byView[config.view_context]) {
                byView[config.view_context] = {};
              }
              const viewPanels = byView[config.view_context];
              if (viewPanels) {
                viewPanels[config.panel_id] = apiConfigToState(config);
              }
            }
            
            // Merge with existing (don't overwrite views we didn't fetch)
            if (viewContext) {
              state.configsByView[viewContext] = byView[viewContext] || state.configsByView[viewContext] || {};
            } else {
              state.configsByView = { ...state.configsByView, ...byView };
            }
            
            state.lastSyncedAt = new Date().toISOString();
            state.isApiAvailable = true;
          });
        } catch (err) {
          console.error('[PanelConfigStore] Failed to load from API:', err);
          set({ error: err instanceof Error ? err.message : 'Failed to load configurations' });
          
          // Fall back to local storage (already persisted)
        } finally {
          set({ isLoading: false });
        }
      },

      saveToApi: async (viewContext) => {
        const state = get();
        const panels = Object.values(state.configsByView[viewContext] || {});
        
        if (panels.length === 0) return;
        if (state.isSaving) return; // Prevent concurrent saves
        
        set({ isSaving: true, error: null });
        
        try {
          const requests = panels.map((p) => stateToApiRequest(p, viewContext));
          await panelConfigApi.batchUpsertPanelConfigs(requests);
          
          set({ lastSyncedAt: new Date().toISOString() });
        } catch (err) {
          console.error('[PanelConfigStore] Failed to save to API:', err);
          // Don't set error state for background saves, just log
        } finally {
          set({ isSaving: false });
        }
      },

      applyTemplate: async (templateId) => {
        set({ isLoading: true, error: null });
        
        try {
          const result = await panelConfigApi.applyTemplate(templateId);
          
          // Reload configs for the view
          await get().loadFromApi(result.view_context);
          
        } catch (err) {
          console.error('[PanelConfigStore] Failed to apply template:', err);
          set({ error: err instanceof Error ? err.message : 'Failed to apply template' });
        } finally {
          set({ isLoading: false });
        }
      },

      loadTemplates: async (viewContext) => {
        try {
          const templates = await panelConfigApi.listTemplates(viewContext);
          set({ templates });
        } catch (err) {
          console.error('[PanelConfigStore] Failed to load templates:', err);
        }
      },

      setCurrentView: (viewContext) => {
        set({ currentView: viewContext });
      },

      getPanelsForView: (viewContext) => {
        const state = get();
        const viewPanels = state.configsByView[viewContext] || {};
        return Object.values(viewPanels).sort((a, b) => a.zIndex - b.zIndex);
      },

      getPanel: (viewContext, panelId) => {
        const state = get();
        return state.configsByView[viewContext]?.[panelId];
      },

      setApiAvailable: (available) => {
        set({ isApiAvailable: available });
      },
    })),
    {
      name: 'panel-config-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        configsByView: state.configsByView,
        currentView: state.currentView,
      }),
    }
  )
);

// ============================================================================
// SELECTORS
// ============================================================================

export const selectPanelsForCurrentView = (state: PanelConfigStore) =>
  state.getPanelsForView(state.currentView);

export const selectVisiblePanels = (state: PanelConfigStore) =>
  state.getPanelsForView(state.currentView).filter((p) => p.isVisible);

export const selectIsSynced = (state: PanelConfigStore) =>
  state.lastSyncedAt !== null && !state.isSaving;

// ============================================================================
// EXPORTS
// ============================================================================

export default usePanelConfigStore;
